import { generateStableIdFromName } from "../../shared/id";
/**
 * High‑level staking protocol categories.
 */
export var StakingProtocolType;
(function (StakingProtocolType) {
    StakingProtocolType["LiquidStaking"] = "liquid-staking";
    StakingProtocolType["NativeStaking"] = "native-staking";
})(StakingProtocolType || (StakingProtocolType = {}));
/**
 * Base abstraction for all staking protocol implementations.
 *
 * @remarks
 * Concrete implementations (e.g., StakeWise) must extend this class and implement
 * the abstract methods for building transactions, querying balances, managing
 * rewards, and handling withdrawals. The surface area reflects the current
 * StakeWise model; future protocols may extend or refine it as needed.
 *
 * Example: extend this class and implement all abstract methods to integrate a
 * new staking protocol (see StakeWise implementation for reference).
 */
export class StakingProtocol {
    id;
    name;
    description;
    url;
    logoURI;
    availableChains;
    stakingProtocolType;
    logger;
    chainService;
    /** Optional TrueUps referral address propagated from SDK configuration */
    referralTag;
    protocolConfig;
    /**
     * Creates a new staking protocol instance.
     *
     * @param options - Protocol configuration and dependencies
     * @param options.logger - Logger instance for protocol logs
     * @param options.name - Human‑readable protocol name
     * @param options.description - Short description of the protocol
     * @param options.url - Optional protocol website URL
     * @param options.logoURI - Optional protocol logo URI
     * @param options.availableChains - Supported chains in CAIP‑2 format
     * @param options.stakingProtocolType - Protocol category (liquid/native)
     * @param options.chainService - Chain service dependency
     * @param options.referralTag - Optional TrueUps referral address
     * @param options.bdApiKey - Blockdaemon API key
     * @param options.protocolConfig - Protocol-specific configuration
     */
    constructor(options) {
        this.logger = options.logger;
        this.name = options.name;
        this.description = options.description;
        this.url = options.url;
        this.logoURI = options.logoURI;
        this.id = generateStableIdFromName(options.name);
        this.availableChains = options.availableChains;
        this.stakingProtocolType = options.stakingProtocolType;
        this.chainService = options.chainService;
        this.referralTag = options.referralTag;
        this.protocolConfig = options.protocolConfig;
    }
    // ========== HELPER METHODS ==========
    /**
     * Get protocol definition metadata.
     * @internal
     */
    getProtocolDefinition() {
        return {
            name: this.name,
            description: this.description,
            url: this.url,
            logoURI: this.logoURI,
            availableChains: this.availableChains,
            stakingProtocolType: this.stakingProtocolType,
        };
    }
    // ========== GETTER METHODS ==========
    /** Returns the stable protocol identifier derived from its name */
    getID() {
        return this.id;
    }
    /** Returns the human‑readable protocol name */
    getName() {
        return this.name;
    }
    /** Returns the protocol description */
    getDescription() {
        return this.description;
    }
    /** Returns the protocol website URL, if provided */
    getURL() {
        return this.url;
    }
    /** Returns the protocol logo URI, if provided */
    getLogoURI() {
        return this.logoURI;
    }
    /** Returns the logger instance used by the protocol */
    getLogger() {
        return this.logger;
    }
}
//# sourceMappingURL=data:application/json;base64,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